import {
  buildScoutInternalEventMessage,
  parseRespondedScoutInternalEventsFromMessages,
  type ChatMessageLike,
  type ScoutInternalEventStatus,
} from "./scoutInternalEvents";

export type LargeFileFullAnalysisEventStatus = ScoutInternalEventStatus;

function pluralizeDocument(count: number): string {
  return count === 1 ? "document" : "documents";
}

function formatExtractionWatchMarkers(privateDocumentIds: string[]): string {
  return privateDocumentIds
    .map((privateDocumentId) => `[scout-extraction-watch:${privateDocumentId}]`)
    .join("\n");
}

/**
 * Build the synthetic user message for a large-file full-analysis resolution.
 *
 * Confirm path appends extraction-watch markers so existing UI parsers
 * (`UserMessage`, retry payload) still discover watched document ids. The
 * chat hides the `[scout-internal-event]` line; markers are stripped from
 * visible bubble text separately.
 */
export function buildLargeFileFullAnalysisEventMessage(input: {
  status: LargeFileFullAnalysisEventStatus;
  proposalId: string;
  /** Documents confirmed or declined in this resolution. */
  count: number;
  /** Required on confirm so extraction-watch markers ride along. */
  privateDocumentIds?: string[];
}): string {
  const fileWord = pluralizeDocument(input.count);
  if (input.status === "confirmed") {
    const header = buildScoutInternalEventMessage({
      kind: "large-file-full-analysis",
      status: "confirmed",
      proposalId: input.proposalId,
      detail:
        `${input.count} ${fileWord}. ` +
        `Full analysis finished. Skip any acknowledgement; immediately answer the user's previous question using the complete document tools.`,
    });
    const privateDocumentIds = input.privateDocumentIds ?? [];
    if (privateDocumentIds.length === 0) {
      return header;
    }
    return `${header}\n${formatExtractionWatchMarkers(privateDocumentIds)}`;
  }

  return buildScoutInternalEventMessage({
    kind: "large-file-full-analysis",
    status: "declined",
    proposalId: input.proposalId,
    detail:
      `${input.count} ${fileWord}. ` +
      `User declined full analysis. Continue using preview only. Do not re-prompt for full analysis on these documents.`,
  });
}

/**
 * Scan persisted history for large-file full-analysis events and return
 * `proposalId -> status`. Pending state still comes from
 * `extractionStatus`; this map is for responded-state consistency with
 * other proposal kinds.
 */
export function parseRespondedLargeFileFullAnalysisFromMessages(
  messages: ReadonlyArray<ChatMessageLike>
): Record<string, LargeFileFullAnalysisEventStatus> {
  return parseRespondedScoutInternalEventsFromMessages(messages, "large-file-full-analysis");
}
